import type { ReactNode } from 'react'
import type { Plan } from '../types/Plan'
import { PlanComparisonFeatures } from './planComparisonData'

export enum PlanFeatures {
  liveAgents = 'Live Agents',
  cameraRecordings = 'Unlimited Camera Recordings',
  allCoreFeatures = 'All Core Features'
}

export type PlanFeature = {
  readonly feature: PlanFeatures
  readonly description: ReactNode
}

type PlanData = {
  readonly description: string
  readonly features: readonly PlanFeature[]
  readonly comparisonFeatures: ReadonlyMap<PlanComparisonFeatures, ReactNode>
}

export type HybridPlanData = Record<string, PlanData>

export const hybridPlanData: HybridPlanData = {
  SSEDSM2__4867366: {
    description: '24/7 professional monitoring by a team of live agents',
    features: [
      {
        feature: PlanFeatures.liveAgents,
        description: (
          <>
            Live agents monitor for emergencies & request fast police dispatch{' '}
            <strong>24/7</strong>
          </>
        )
      },
      {
        feature: PlanFeatures.cameraRecordings,
        description: 'Unlimited video recordings & 30-day storage'
      }
    ],
    comparisonFeatures: new Map([
      [PlanComparisonFeatures.professionalMonitoring, null],
      [PlanComparisonFeatures.intruderIntervention, null],
      [PlanComparisonFeatures.cameraRecording, null],
      [PlanComparisonFeatures.appControl, null],
      [PlanComparisonFeatures.cellularConnection, null],
      [PlanComparisonFeatures.noContract, null],
      [PlanComparisonFeatures.videoVerification, null]
    ])
  },
  SSODMONON__13798447: {
    description:
      'Core + overnight crime prevention with Active Guard Outdoor Protection',
    features: [
      {
        feature: PlanFeatures.allCoreFeatures,
        description: 'All Core features'
      },
      {
        feature: PlanFeatures.liveAgents,
        description: (
          <>
            Live agents deter potential intruders using outdoor cameras{' '}
            <strong>overnight (8pm-6am)</strong>
          </>
        )
      }
    ],
    comparisonFeatures: new Map([
      [PlanComparisonFeatures.professionalMonitoring, null],
      [PlanComparisonFeatures.videoVerification, null],
      [PlanComparisonFeatures.intruderIntervention, null],
      [PlanComparisonFeatures.cameraRecording, null],
      [
        PlanComparisonFeatures.activeGuard,
        <>
          <span className="block">Available 8pm-6am,</span>
          <span className="block">local time</span>
        </>
      ],
      [PlanComparisonFeatures.appControl, null],
      [PlanComparisonFeatures.cellularConnection, null],
      [PlanComparisonFeatures.noContract, null]
    ])
  },
  SSODMON247__13798553: {
    description:
      'Core + 24/7 crime prevention with Active Guard Outdoor Protection',
    features: [
      {
        feature: PlanFeatures.allCoreFeatures,
        description: 'All Core features'
      },
      {
        feature: PlanFeatures.liveAgents,
        description: (
          <>
            Live agents deter potential intruders using outdoor cameras{' '}
            <strong>24/7</strong>
          </>
        )
      }
    ],
    comparisonFeatures: new Map([
      [PlanComparisonFeatures.professionalMonitoring, null],
      [PlanComparisonFeatures.videoVerification, null],
      [PlanComparisonFeatures.intruderIntervention, null],
      [PlanComparisonFeatures.cameraRecording, null],
      [
        PlanComparisonFeatures.activeGuard,
        <span className="block">Available 24/7</span>
      ],
      [PlanComparisonFeatures.appControl, null],
      [PlanComparisonFeatures.cellularConnection, null],
      [PlanComparisonFeatures.noContract, null]
    ])
  }
}

export const coreMonitoringPlanData: Plan = {
  freeMonths: 1,
  isDefault: true,
  isResidential: true,
  name: 'Core Monitoring',
  pricePerDay: '$1.10',
  pricePerMonth: '$30',
  shortName: 'Core',
  sku: 'SSEDSM2__4867366',
  type: 'interactive'
}

export const proMonitoringPlanData: Plan = {
  freeMonths: 1,
  isDefault: true,
  isResidential: true,
  name: 'Pro Monitoring',
  pricePerDay: '$1.66',
  pricePerMonth: '$50',
  shortName: 'Pro',
  sku: 'SSODMONON__13798447',
  type: 'odmonOvernight'
}

export const proPlusMonitoringPlanData: Plan = {
  freeMonths: 1,
  isDefault: false,
  isResidential: true,
  name: 'Pro Plus Monitoring',
  pricePerDay: '$2.66',
  pricePerMonth: '$80',
  shortName: 'Pro Plus',
  sku: 'SSODMON247__13798553',
  type: 'odmon247'
}

export const odmonPlansData = [
  {
    ...coreMonitoringPlanData,
    isDefault: false
  },
  proMonitoringPlanData,
  proPlusMonitoringPlanData
]
